import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Activity, ActivityFormData, ActivityFilters } from '@/types/crm';

interface ActivityStore {
  activities: Activity[];
  isLoading: boolean;
  filters: ActivityFilters;
  fetchActivities: () => Promise<void>;
  fetchActivitiesByEntity: (entityType: 'lead' | 'contact' | 'company' | 'deal', entityId: string) => Promise<Activity[]>;
  createActivity: (data: ActivityFormData) => Promise<Activity | null>;
  updateActivity: (id: string, data: Partial<ActivityFormData>) => Promise<void>;
  completeActivity: (id: string, outcome?: string) => Promise<void>;
  deleteActivity: (id: string) => Promise<void>;
  setFilters: (filters: ActivityFilters) => void;
}

export const useActivityStore = create<ActivityStore>((set, get) => ({
  activities: [],
  isLoading: false,
  filters: {},

  fetchActivities: async () => {
    set({ isLoading: true });
    try {
      const { filters } = get();
      let query = supabase
        .from('crm_activities_with_details')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.is_completed !== undefined) {
        query = query.eq('is_completed', filters.is_completed);
      }
      if (filters.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }
      if (filters.date_from) {
        query = query.gte('due_date', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('due_date', filters.date_to);
      }
      if (filters.search) {
        query = query.or(`subject.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      set({ activities: data || [] });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchActivitiesByEntity: async (entityType: 'lead' | 'contact' | 'company' | 'deal', entityId: string) => {
    const columnMap = {
      lead: 'lead_id',
      contact: 'contact_id',
      company: 'company_id',
      deal: 'deal_id',
    };

    const { data, error } = await supabase
      .from('crm_activities_with_details')
      .select('*')
      .eq(columnMap[entityType], entityId)
      .order('created_at', { ascending: false });

    if (error) return [];
    return data || [];
  },

  createActivity: async (data: ActivityFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: activity, error } = await supabase
      .from('crm_activities')
      .insert({
        ...data,
        created_by: user.id,
        assigned_to: data.assigned_to || user.id,
      })
      .select()
      .single();

    if (error) throw error;
    await get().fetchActivities();
    return activity;
  },

  updateActivity: async (id: string, data: Partial<ActivityFormData>) => {
    const { error } = await supabase
      .from('crm_activities')
      .update(data)
      .eq('id', id);

    if (error) throw error;
    await get().fetchActivities();
  },

  completeActivity: async (id: string, outcome?: string) => {
    const { error } = await supabase
      .from('crm_activities')
      .update({
        is_completed: true,
        completed_at: new Date().toISOString(),
        outcome,
      })
      .eq('id', id);

    if (error) throw error;
    await get().fetchActivities();
  },

  deleteActivity: async (id: string) => {
    const { error } = await supabase
      .from('crm_activities')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await get().fetchActivities();
  },

  setFilters: (filters: ActivityFilters) => set({ filters }),
}));
